import { ColDef, GridApi } from '@ag-grid-community/core'
import _ from 'lodash-es'
import {
  ColumnTypeOverride,
  isColumnTypeOverride,
} from '../models/column-type-override.model'
import {
  ColumnFilterModel,
  ColumnTypeOverrideContext,
} from '../models/filter-editor.model'
import { FilterOperator } from '../models/filter-operator.model'
import { FilterValueInputType } from '../models/filter.model'
import {
  getColumnFilterModels,
  GetColumnFilterModelsOptions,
} from './column-filter-utils'
import { getFilterValueInputType } from './filter-type-utils'

/**
 * Override-aware column-filter models, for grids with AGGREGATE_INFO columns (PVAL-26423).
 *
 * This is a WRAPPER around the unmodified {@link getColumnFilterModels}, deliberately kept in its own
 * file: `column-filter-utils.ts` is consumed by Positions 1.0, account-list, multi-breaks,
 * external-transactions, distributions and clearwater-transactions, and none of them should see a
 * diff in a file they already depend on for the sake of a feature only two panels use. Panels that
 * want override awareness provide {@link AggInfoFilterModalService}; everyone else keeps the base
 * behaviour byte-for-byte.
 */

/**
 * The operators offered per chosen comparison type.
 *
 * This is the ONE vocabulary: the grid-header filter component and the filter modal must offer the
 * same operators for the same column, or a predicate built in one surface would be unreachable in the
 * other. It is ag-Grid's own simple-filter operator vocabulary, because both surfaces emit ag-Grid's
 * simple-filter model shape — which is what `@helios/server-side-util`'s `convertFilters` knows how
 * to translate into the /query contract. Inventing an operator here would produce a model the
 * converter drops on the floor, silently un-filtering the column.
 */
export const COLUMN_TYPE_OVERRIDE_OPERATORS: Record<
  ColumnTypeOverride,
  readonly string[]
> = {
  text: [
    'contains',
    'notContains',
    'equals',
    'notEqual',
    'startsWith',
    'endsWith',
    'blank',
    'notBlank',
  ],
  number: [
    'equals',
    'notEqual',
    'greaterThan',
    'greaterThanOrEqual',
    'lessThan',
    'lessThanOrEqual',
    'inRange',
    'blank',
    'notBlank',
  ],
  date: ['equals', 'greaterThan', 'lessThan', 'inRange', 'blank', 'notBlank'],
}

/** Display names for {@link COLUMN_TYPE_OVERRIDE_OPERATORS}, shared by both surfaces. */
export const COLUMN_TYPE_OVERRIDE_OPERATOR_LABELS: Record<string, string> = {
  contains: 'Contains',
  notContains: 'Not contains',
  equals: 'Equals',
  notEqual: 'Not equal',
  startsWith: 'Starts with',
  endsWith: 'Ends with',
  greaterThan: 'Greater than',
  greaterThanOrEqual: 'Greater than or equal',
  lessThan: 'Less than',
  lessThanOrEqual: 'Less than or equal',
  inRange: 'In range',
  blank: 'Blank',
  notBlank: 'Not blank',
}

/** The {@link FilterOperator} list for a chosen comparison type. */
export const operatorsForColumnType = (
  type: ColumnTypeOverride,
): FilterOperator[] =>
  COLUMN_TYPE_OVERRIDE_OPERATORS[type].map(operator => ({
    type: operator,
    name:
      COLUMN_TYPE_OVERRIDE_OPERATOR_LABELS[operator] ?? _.startCase(operator),
  }))

/**
 * A column whose comparison type the USER owns rather than the catalog — see
 * {@link ColumnTypeOverrideContext}.
 *
 * Read off the ColDef rather than the override service so this stays a pure function: the panel that
 * builds the ColDefs already knows both the scope and the resolved choice, and stamps them into
 * `filterParams`.
 */
export function readTypeOverride(
  colDef: ColDef,
): ColumnTypeOverrideContext | undefined {
  const declared = colDef.filterParams?.columnTypeOverride as
    | Partial<ColumnTypeOverrideContext>
    | undefined
  if (!declared?.scope || !isColumnTypeOverride(declared.type)) return undefined
  return { scope: declared.scope, type: declared.type }
}

/**
 * {@link getColumnFilterModels}, plus the columns whose comparison type the user owns.
 *
 * An AGGREGATE_INFO column carries a custom grid-header filter component rather than one of the
 * string filter ids the base `getFilterType` switches on, so the base function cannot type it and
 * DROPS it — the column would simply be missing from the Filters modal. Rather than teach the shared
 * util about overrides, the dropped columns are rebuilt here from the chosen type, using the same
 * public helpers the base uses for everything else.
 */
export function getAggInfoAwareColumnFilterModels(
  gridApi: GridApi,
  options?: GetColumnFilterModelsOptions,
): ColumnFilterModel[] {
  const base = getColumnFilterModels(gridApi, options)
  const byField = new Map(base.map(model => [model.field, model]))

  const colDefs = gridApi.getColumnDefs() ?? []
  colDefs.forEach(colDef => {
    const field = (colDef as ColDef).field
    if (!field || options?.omitColumns.includes(field)) return

    const typeOverride = readTypeOverride(colDef as ColDef)
    if (!typeOverride) return

    byField.set(
      field,
      retypedColumnFilterModel(
        byField.get(field) ?? {
          columnName: (colDef as ColDef).headerName ?? _.startCase(field),
          field,
          defaultOperator: undefined,
          // Replaced wholesale below; present only to satisfy the model's shape.
          filterType: { type: 'text' },
          operators: [],
          filterValueInputs: new Map(),
        },
        typeOverride,
      ),
    )
  })

  // Same ordering contract as the base function — the modal lists columns alphabetically.
  return _.sortBy([...byField.values()], model => model.columnName)
}

/**
 * Re-derive a column-filter model for a chosen comparison type: the type's operators, the value
 * input each of them takes, and the override context the editor row needs to render its type picker.
 *
 * Exported because it is needed in two places that must not drift — building the modal's initial
 * models, and re-deriving one in place when the user picks a different type while the modal is open.
 * Everything not type-dependent is preserved from `model`.
 */
export function retypedColumnFilterModel(
  model: ColumnFilterModel,
  typeOverride: ColumnTypeOverrideContext,
): ColumnFilterModel {
  const filterType = { type: typeOverride.type } as const
  const operators = operatorsForColumnType(typeOverride.type)

  const filterValueInputs = new Map<string, FilterValueInputType>()
  operators.forEach(operator => {
    const inputType = getFilterValueInputType(filterType, operator.type)
    if (inputType) filterValueInputs.set(operator.type, inputType)
  })

  return {
    ...model,
    filterType,
    operators,
    filterValueInputs,
    typeOverride,
  }
}
